import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAuth, useQueryParams, useTerritories } from "../contexts";
import { getRequestOptions } from "../helper-functions";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import {
    DefaultPagedResponse,
    Entity,
    PagedResponse,
    QueryParams,
    RecoveryAppImport,
    QueryResult,
    MutationResult,
    AppDataSyncRequest,
    Case,
    RecoveryDashboardData,
    Donor,
} from "../models";

interface IUseRecoveryQuery {
    dashboardQuery: QueryResult<RecoveryDashboardData>;
    pagedFailedSyncsQuery: QueryResult<PagedResponse<RecoveryAppImport>>;
    manualSyncOptionsQuery: QueryResult<Case[]>;
    manualSyncAppImportMutation: MutationResult<Donor, unknown, AppDataSyncRequest>;
    manualCreateDonorAndTissuesMutation: MutationResult<Donor, unknown, number>;
}

export const useRecoveryQuery = (): IUseRecoveryQuery => {
    const { isAuthorized } = useAuth();
    const { get, post, patch } = useAxios();
    const { selectedTerritoryIds } = useTerritories();
    const { queryParams, sortParams, pageParams } = useQueryParams().getParamsApi(Entity.Accounts);
    const url = Endpoints.Recoveries;

    const queryParamsWithArgs: QueryParams = {
        ...queryParams,
        territoryIds: selectedTerritoryIds.join(","),
    };

    const dashboardQuery = useGenericQuery({
        queryFn: (config) =>
            get<RecoveryDashboardData>(getRequestOptions(`${url}/dashboard`, config, queryParamsWithArgs)),
        queryKey: queryKeys.dashboards.recovery(queryParamsWithArgs),
        placeholderData: {} as RecoveryDashboardData,
        enabled: isAuthorized,
        staleTime: 5000,
        cacheTime: 5000,
    });

    const pagedFailedSyncsQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<RecoveryAppImport>>(
                getRequestOptions(`${url}/imports/failed`, config, queryParamsWithArgs, sortParams, pageParams)
            ),
        queryKey: queryKeys.recoveryAppImports.pagedList({ ...queryParamsWithArgs, ...pageParams, ...sortParams }),
        placeholderData: DefaultPagedResponse,
        enabled: isAuthorized,
        staleTime: 5000,
        cacheTime: 5000,
    });

    const manualSyncOptionsQuery = useGenericQuery({
        queryFn: (config) => get<Case[]>(getRequestOptions(`${url}/imports/sync-options`, config, queryParamsWithArgs)),
        queryKey: queryKeys.recoveryAppImports.list(queryParamsWithArgs),
        placeholderData: [],
        enabled: isAuthorized,
        staleTime: 5000,
        cacheTime: 5000,
    });

    const manualSyncAppImportMutation = useGenericMutation({
        mutationFn: (data: AppDataSyncRequest) =>
            patch<Donor>({ url: `${url}/imports/${data.recoveryAppImportId}/sync?caseId=${data.caseId}` }),
        queryKey: queryKeys.recoveryAppImports.details(),
        successToast: "Data sync successful",
        keysToInvalidate: [queryKeys.recoveryAppImports.pagedLists()],
    });

    const manualCreateDonorAndTissuesMutation = useGenericMutation({
        mutationFn: (caseId: number) => post<Donor>({ url: `${url}/case/${caseId}` }),
        queryKey: queryKeys.recoveryAppImports.details(),
        successToast: "Donor and tissues created successfully",
        keysToInvalidate: [queryKeys.recoveryAppImports.pagedLists(), queryKeys.cases.details()],
    });

    return useMemo<IUseRecoveryQuery>(
        () => ({
            dashboardQuery,
            pagedFailedSyncsQuery,
            manualSyncOptionsQuery,
            manualSyncAppImportMutation,
            manualCreateDonorAndTissuesMutation,
        }),
        [
            dashboardQuery,
            manualCreateDonorAndTissuesMutation,
            manualSyncAppImportMutation,
            manualSyncOptionsQuery,
            pagedFailedSyncsQuery,
        ]
    );
};
